import { useState, useEffect } from "react";
import api from "@/utils/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shield, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AiConsentDialog({ isOpen, onClose, onConsented }) {
  const [loading, setLoading] = useState(false);

  const handleConsent = async (consent) => {
    setLoading(true);
    try {
      await api.post("/user/ai-consent", { consent });
      if (consent) {
        toast.success("KI-Belegerkennung aktiviert");
        onConsented?.();
      } else {
        toast.info("Lokale OCR wird verwendet");
      }
      onClose();
    } catch (e) {
      toast.error("Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px]" data-testid="ai-consent-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            KI-Belegerkennung aktivieren
          </DialogTitle>
          <DialogDescription>
            Datenschutz-Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <p className="font-medium mb-2">Was passiert mit deinen Daten?</p>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">&#8226;</span>
                Belegbilder werden an einen KI-Dienst (OpenAI/Anthropic, USA) zur Analyse übermittelt
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">&#8226;</span>
                Nur Bilddaten werden gesendet – keine persönlichen Nutzerdaten
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">&#8226;</span>
                Auftragsverarbeitungsverträge (AVV/DPA) mit EU-Standardvertragsklauseln sind abgeschlossen
              </li>
            </ul>
          </div>

          <div className="bg-slate-50 border rounded-lg p-3 text-sm">
            <p className="font-medium text-slate-800 mb-1">Dein Recht</p>
            <p className="text-slate-600">
              Du kannst diese Einwilligung jederzeit in den Einstellungen widerrufen. 
              Ohne KI-Einwilligung wird die kostenlose lokale OCR-Erkennung verwendet.
            </p>
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-500">
            <ExternalLink className="h-3.5 w-3.5" />
            <a href="/privacy" target="_blank" className="underline hover:text-slate-700">
              Datenschutzerklärung §20
            </a>
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => handleConsent(false)}
            disabled={loading}
            data-testid="ai-consent-decline"
          >
            Nein, lokale OCR nutzen
          </Button>
          <Button
            onClick={() => handleConsent(true)}
            disabled={loading}
            data-testid="ai-consent-accept"
          >
            {loading ? "Wird gespeichert..." : "Einwilligen & aktivieren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function AiConsentToggle() {
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/user/ai-consent");
        setConsent(res.data.ai_consent);
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleToggle = async (checked) => {
    if (checked) {
      setShowDialog(true);
    } else {
      try {
        await api.post("/user/ai-consent", { consent: false });
        setConsent(false);
        toast.info("KI-Belegerkennung deaktiviert. Lokale OCR wird verwendet.");
      } catch (e) {
        toast.error("Fehler beim Speichern");
      }
    }
  };

  if (loading) return null;

  return (
    <>
      <div className="flex items-center justify-between" data-testid="ai-consent-toggle">
        <div>
          <Label className="text-sm font-medium">KI-Belegerkennung</Label>
          <p className="text-xs text-slate-500 mt-0.5">
            {consent
              ? "Aktiv – Belege werden per KI analysiert (DSGVO-Einwilligung erteilt)"
              : "Inaktiv – Lokale OCR-Erkennung wird verwendet"}
          </p>
        </div>
        <Switch
          checked={consent}
          onCheckedChange={handleToggle}
          data-testid="ai-consent-switch"
        />
      </div>

      <AiConsentDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        onConsented={() => setConsent(true)}
      />
    </>
  );
}


/**
 * Bank-AI-Consent-Toggle (Sprint 3, Feb 2026).
 *
 * Identische DSGVO-Architektur wie AiConsentToggle, aber für Bankauszug-OCR.
 * Pro-Plan-Pflicht — der Toggle wird disabled gerendert, wenn der User keinen
 * Pro-Plan hat. Backend prüft beides erneut beim eigentlichen Import.
 */
export function BankAiConsentToggle() {
  const [consent, setConsent] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/me/bank-ai-consent");
        setConsent(res.data.bank_ai_consent);
        setIsPro(!!res.data.is_pro);
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleToggle = async (checked) => {
    if (checked && !isPro) {
      toast.error("Pro-Plan erforderlich für KI-Kontoauszug-Erkennung");
      return;
    }
    try {
      await api.post("/me/bank-ai-consent", { consent: checked });
      setConsent(checked);
      toast.success(checked
        ? "KI-Kontoauszug-Erkennung aktiviert"
        : "KI-Kontoauszug-Erkennung deaktiviert");
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  if (loading) return null;

  return (
    <div className="flex items-center justify-between" data-testid="bank-ai-consent-toggle">
      <div className="flex-1 pr-4">
        <Label className="text-sm font-medium flex items-center gap-2">
          KI-Kontoauszug-Erkennung
          {!isPro && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
              Pro
            </span>
          )}
        </Label>
        <p className="text-xs text-slate-500 mt-0.5">
          {!isPro
            ? "Im Pro-Plan kannst du PDF/Bild-Kontoauszüge per KI auslesen lassen."
            : consent
              ? "Aktiv – PDF/Bild-Kontoauszüge werden per KI analysiert (DSGVO-Einwilligung erteilt)"
              : "Inaktiv – Nur CAMT.053-XML und Bank-CSV werden lokal verarbeitet"}
        </p>
      </div>
      <Switch
        checked={consent && isPro}
        disabled={!isPro}
        onCheckedChange={handleToggle}
        data-testid="bank-ai-consent-switch"
      />
    </div>
  );
}
